import React, { useEffect, useState } from 'react';
import {
  Box, Card, Table, TableHead, TableRow, TableCell, TableBody,
  Button, IconButton, Typography, Chip, TextField, InputAdornment, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  useMediaQuery, useTheme,
} from '@mui/material';
import {
  Add, Edit, PictureAsPdf, Search, Delete, Visibility, FileDownload, LocalShipping,
  ContentCopy, Check, Close,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import PageHeader from '../common/PageHeader';
import { useAuth } from '../../contexts/AuthContext';
import { buildShippingInvoiceXLSX } from './buildShippingInvoiceXLSX';

const STATUS_COLORS = {
  draft: 'default', ready: 'info', shipped: 'primary', received: 'success', cancelled: 'error',
};
const FWD_LABEL = {
  yitong_jimmy: 'Yitong / Jimmy', fedex_china: 'FedEx China', self: 'Self', other: 'Other',
};
// FedEx public tracking page for a given AWB / tracking number.
const fedexTrackUrl = (awb) => `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(String(awb).trim())}`;

export default function ShippingInvoiceList() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  const canEdit = ['admin', 'manager'].includes(user?.role);
  const canDelete = user?.role === 'admin';
  const navigate = useNavigate();

  const [list, setList] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deleteDialog, setDeleteDialog] = useState(null);

  const load = async () => {
    const res = await api.get('/shipping-invoices');
    setList(res.data.data || []);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    let d = list;
    if (search) {
      const q = search.toLowerCase();
      d = d.filter(s => (s.invoice_number || '').toLowerCase().includes(q)
        || (s.beneficiary_name || '').toLowerCase().includes(q)
        || (s.proforma_number || '').toLowerCase().includes(q)
        || (s.fedex_awb || '').toLowerCase().includes(q));
    }
    if (statusFilter !== 'all') d = d.filter(s => s.status === statusFilter);
    setFiltered(d);
  }, [search, statusFilter, list]);

  // Inline FedEx AWB editing straight from the list (PUT is a partial update,
  // so only fedex_awb changes — items/totals/forwarder are untouched).
  const [awbEdit, setAwbEdit] = useState(null); // { id, value }
  const [awbSaving, setAwbSaving] = useState(false);
  const saveAwb = async () => {
    if (!awbEdit || awbSaving) return;
    const value = awbEdit.value.trim();
    setAwbSaving(true);
    try {
      await api.put(`/shipping-invoices/${awbEdit.id}`, { fedex_awb: value });
      setList(prev => prev.map(r => (r.id === awbEdit.id ? { ...r, fedex_awb: value || null } : r)));
      setAwbEdit(null);
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to save AWB');
    } finally { setAwbSaving(false); }
  };
  const awbEditor = (s) => (
    <TextField size="small" autoFocus value={awbEdit.value} placeholder="AWB number"
      onChange={e => setAwbEdit({ id: s.id, value: e.target.value })}
      onKeyDown={e => { if (e.key === 'Enter') saveAwb(); if (e.key === 'Escape') setAwbEdit(null); }}
      inputProps={{ style: { fontFamily: 'monospace', fontSize: 12, padding: '4px 8px' }, maxLength: 30 }}
      sx={{ width: 190 }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton size="small" disabled={awbSaving} onClick={saveAwb} sx={{ color: 'success.main', p: 0.25 }}><Check fontSize="small" /></IconButton>
            <IconButton size="small" onClick={() => setAwbEdit(null)} sx={{ p: 0.25 }}><Close fontSize="small" /></IconButton>
          </InputAdornment>
        ),
      }}
    />
  );

  const handleDelete = async () => {
    try {
      await api.delete(`/shipping-invoices/${deleteDialog.id}`);
      setDeleteDialog(null); load();
    } catch { setDeleteDialog(null); }
  };

  const openPdf = async (id, ref) => {
    try {
      const res = await api.get(`/shipping-invoices/${id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `${ref.replace(/\//g, '-')}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
  };

  const exportExcel = async (id) => {
    try {
      const r = await api.get(`/shipping-invoices/${id}`);
      const d = r.data.data;
      buildShippingInvoiceXLSX(d, d.items || []);
    } catch (e) { console.error(e); alert('Excel export failed'); }
  };

  return (
    <Box>
      <PageHeader title="Shipping Invoices" subtitle="Commercial invoices for FedEx-shipped consignments from suppliers"
        action={
          <Button variant="contained" startIcon={<Add />} size={isMobile ? 'small' : 'medium'}
            onClick={() => navigate('/shipping-invoices/new')}>
            {isMobile ? 'New' : 'Create Shipping Invoice'}
          </Button>
        }
      />

      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <TextField size="small" placeholder="Search by number, supplier, PI, AWB…" value={search} onChange={e => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 200 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
        />
        <TextField size="small" select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          sx={{ minWidth: 140 }} label="Status" SelectProps={{ native: true }}>
          <option value="all">All Status</option>
          <option value="draft">Draft</option>
          <option value="ready">Ready</option>
          <option value="shipped">Shipped</option>
          <option value="received">Received</option>
          <option value="cancelled">Cancelled</option>
        </TextField>
      </Box>

      {isMobile ? (
        // Mobile card view
        filtered.length === 0 ? (
          <Card sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>No shipping invoices.</Card>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {filtered.map(s => (
              <Card key={s.id} sx={{ p: 1.5, cursor: 'pointer', touchAction: 'manipulation' }}
                onClick={() => navigate(`/shipping-invoices/${s.id}`)}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.75 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontFamily: 'monospace', fontSize: 11, color: 'text.secondary' }}>{s.invoice_number}</Typography>
                    <Typography variant="body2" fontWeight={700}>{s.beneficiary_name}</Typography>
                  </Box>
                  <Chip size="small" label={s.status} color={STATUS_COLORS[s.status]} variant={s.status === 'draft' ? 'outlined' : 'filled'} />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <Typography variant="caption" color="text.secondary">{s.ship_date}</Typography>
                  <Typography variant="body2" fontWeight={600}>{s.currency} {parseFloat(s.total_value).toFixed(2)}</Typography>
                </Box>
                {s.proforma_number && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'primary.main' }}>
                    PI: {s.proforma_number}
                  </Typography>
                )}
                <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }} onClick={e => e.stopPropagation()}>
                  {awbEdit?.id === s.id ? awbEditor(s) : (
                    <>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        AWB:{' '}
                        {s.fedex_awb
                          ? <a href={fedexTrackUrl(s.fedex_awb)} target="_blank" rel="noopener noreferrer"
                               style={{ color: '#4d148c', fontWeight: 600, textDecoration: 'underline' }}>{s.fedex_awb}</a>
                          : '—'}
                      </Typography>
                      {canEdit && (
                        <Button size="small" sx={{ fontSize: 10, py: 0, minWidth: 0, textTransform: 'none' }}
                          onClick={() => setAwbEdit({ id: s.id, value: s.fedex_awb || '' })}>
                          {s.fedex_awb ? 'Edit' : 'Add'}
                        </Button>
                      )}
                    </>
                  )}
                </Box>
                {canEdit && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5, gap: 0.25 }} onClick={(e) => e.stopPropagation()}>
                    <IconButton size="small" onClick={() => navigate(`/shipping-invoices/${s.id}/edit`)}><Edit fontSize="small" /></IconButton>
                    <IconButton size="small" sx={{ color: 'secondary.main' }} onClick={() => navigate(`/shipping-invoices/new?clone=${s.id}`)}><ContentCopy fontSize="small" /></IconButton>
                    <IconButton size="small" sx={{ color: 'error.main' }} onClick={() => openPdf(s.id, s.invoice_number)}><PictureAsPdf fontSize="small" /></IconButton>
                  </Box>
                )}
              </Card>
            ))}
          </Box>
        )
      ) : (
        <Card>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Invoice No.</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Supplier</TableCell>
                <TableCell>Linked PI</TableCell>
                <TableCell>Forwarder</TableCell>
                <TableCell>FedEx AWB</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>No shipping invoices.</TableCell></TableRow>
              ) : filtered.map(s => (
                <TableRow key={s.id} hover>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 600 }}>{s.invoice_number}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{s.ship_date}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>{s.beneficiary_name}</Typography>
                    <Typography variant="caption" color="text.secondary">{s.supplier_country}</Typography>
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                    {s.proforma_number || <Typography variant="caption" color="text.disabled">—</Typography>}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined" icon={<LocalShipping sx={{ fontSize: 12 }} />}
                      label={FWD_LABEL[s.forwarder] || s.forwarder} />
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>
                    {awbEdit?.id === s.id ? awbEditor(s)
                      : s.fedex_awb ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                          <Tooltip title="Track on FedEx (opens new tab)">
                            <a href={fedexTrackUrl(s.fedex_awb)} target="_blank" rel="noopener noreferrer"
                               style={{ color: '#4d148c', fontWeight: 600, textDecoration: 'underline' }}>
                              {s.fedex_awb}
                            </a>
                          </Tooltip>
                          {canEdit && (
                            <Tooltip title="Edit AWB">
                              <IconButton size="small" sx={{ p: 0.25 }} onClick={() => setAwbEdit({ id: s.id, value: s.fedex_awb || '' })}>
                                <Edit sx={{ fontSize: 13 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      ) : canEdit ? (
                        <Button size="small" startIcon={<Add sx={{ fontSize: 14 }} />} sx={{ fontSize: 11, py: 0, minWidth: 0, textTransform: 'none' }}
                          onClick={() => setAwbEdit({ id: s.id, value: '' })}>
                          Add AWB
                        </Button>
                      ) : '—'}
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={600}>{s.currency} {parseFloat(s.total_value).toFixed(2)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={s.status} color={STATUS_COLORS[s.status]} variant={s.status === 'draft' ? 'outlined' : 'filled'} />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="View"><IconButton size="small" sx={{ color: 'info.main' }} onClick={() => navigate(`/shipping-invoices/${s.id}`)}><Visibility fontSize="small" /></IconButton></Tooltip>
                    {canEdit && <Tooltip title="Edit"><IconButton size="small" onClick={() => navigate(`/shipping-invoices/${s.id}/edit`)}><Edit fontSize="small" /></IconButton></Tooltip>}
                    {canEdit && <Tooltip title="Clone (create copy)"><IconButton size="small" sx={{ color: 'secondary.main' }} onClick={() => navigate(`/shipping-invoices/new?clone=${s.id}`)}><ContentCopy fontSize="small" /></IconButton></Tooltip>}
                    <Tooltip title="Download PDF"><IconButton size="small" sx={{ color: 'error.main' }} onClick={() => openPdf(s.id, s.invoice_number)}><PictureAsPdf fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Download Excel"><IconButton size="small" sx={{ color: 'success.main' }} onClick={() => exportExcel(s.id)}><FileDownload fontSize="small" /></IconButton></Tooltip>
                    {canDelete && <Tooltip title="Delete"><IconButton size="small" sx={{ color: 'error.main' }} onClick={() => setDeleteDialog(s)}><Delete fontSize="small" /></IconButton></Tooltip>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={Boolean(deleteDialog)} onClose={() => setDeleteDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Shipping Invoice</DialogTitle>
        <DialogContent>
          <Typography>Delete <strong>{deleteDialog?.invoice_number}</strong>? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
